import type { AbsolutePatchSpec } from './card.js';

/**
 * The physical swatches used as the in-frame reference.
 *
 * `lab` is null for every entry because we do not currently have verified
 * values for them. That is deliberate and load-bearing:
 * {@link buildAbsoluteCard} throws on a null, so the only way to get a working
 * card today is the relative path. When verified numbers arrive — from a
 * manufacturer data sheet or a spectrophotometer reading — fill them in here,
 * set {@link REFERENCE_ILLUMINANT} to match how they were measured, and every
 * consumer upgrades without further changes.
 *
 * Do not populate these by eyedropping a photograph. That is a relative
 * measurement wearing an absolute label, and it would quietly corrupt every
 * reading taken afterwards.
 */
/**
 * Vendor-published data, researched 2026-07-31 from valspar.com. Recorded so
 * the search is not repeated, and deliberately NOT wired into `lab` below.
 *
 *   Swatch                     sRGB           hex       LRV
 *   Elephant Gray 4006-2A      114,122,128    #727a80   18.940
 *   Dark Kettle Black 4011-2    41, 42, 45    #292a2d    2.328
 *   Ultra White 7006-24        248,248,243    #f8f8f3   93.586
 *
 * These are more credible than they look. Converting each published hex to
 * luminance reproduces the published LRV to within 0.12, and for two of the
 * three to within 0.03 — far too close to be coincidence, so Valspar's hex
 * codes are evidently derived from real colorimetric measurements rather than
 * chosen by eye. Third-party colour sites are not: they quote #3f4043 and
 * #434347 for Dark Kettle Black, which differ from the official value by 7.1
 * and 8.2 dE00 respectively. Use valspar.com, not aggregators.
 *
 * They are still not good enough to promote to absolute targets:
 *
 *  - LRV constrains luminance only. It pins L*, and says nothing whatsoever
 *    about a* or b*, which is where a calibration target actually earns its
 *    keep. The chromatic axes rest entirely on an 8-bit web swatch.
 *  - 8-bit quantisation alone is worth 0.8-1.1 dE00 per code value at these
 *    colours, so the round trip through hex has a hard precision floor of
 *    roughly +/-1 dE00 before any other error is considered.
 *  - A published sRGB value is a rendering of the paint, not a measurement of
 *    it. Real Lab depends on measurement geometry (45/0 vs d/8, specular
 *    included or excluded), sheen and substrate, none of which a hex captures.
 *
 * To promote these to `lab`, measure the physical swatches with a
 * spectrophotometer and record the illuminant and geometry alongside. Until
 * then the honest state is null, and calibration stays relative.
 */
export const SWATCH_CATALOG: AbsolutePatchSpec[] = [
  { id: 'elephant-gray-4006-2a', name: 'Elephant Gray 4006-2A', lab: null },
  { id: 'dark-kettle-black-4011-2', name: 'Dark Kettle Black 4011-2', lab: null },
  { id: 'ultra-white-7006-24', name: 'Ultra White 7006-24', lab: null },
];

// Note: all three swatches are near-neutral, spanning only about 6.8 Lab
// chroma units in total. That is not enough to condition a 3x3 fit — see
// MINIMUM_CHROMA_SPREAD in `fit.ts`, which detects this at runtime and falls
// back to a diagonal correction. Any reference photograph intended for
// calibration should include at least one strongly saturated patch alongside
// these three, or the correction is limited to per-channel white balance.

/**
 * Illuminant the absolute values in {@link SWATCH_CATALOG} are measured under.
 *
 * Paint and coatings data is conventionally published under D50. Confirm this
 * against the actual data sheet before trusting absolute results — quoting Lab
 * values under the wrong illuminant is a silent multi-unit error.
 */
export const REFERENCE_ILLUMINANT = 'D50' as const;

/** True once every catalog entry has a verified Lab value. */
export function hasVerifiedReferenceValues(): boolean {
  return SWATCH_CATALOG.every((spec) => spec.lab !== null);
}
